import { Pencil, Plus, Tag, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { ConfirmDialog } from '../../../shared/components/ConfirmDialog';
import { EmptyState } from '../../../shared/components/EmptyState';
import { SkeletonCard } from '../../../shared/components/Skeleton';
import {
  useCategorias,
  useCreateCategoria,
  useDeleteCategoria,
  useUpdateCategoria,
} from '../hooks';
import type { Categoria } from '../types';
import { CategoriaFormModal } from './CategoriaFormModal';

export function CategoriasTab() {
  const { data, isLoading } = useCategorias();
  const createMut = useCreateCategoria();
  const updateMut = useUpdateCategoria();
  const deleteMut = useDeleteCategoria();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Categoria | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Categoria | null>(null);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (c: Categoria) => {
    setEditing(c);
    setFormOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-slate-600">
          {data?.length ?? 0} categoria(s) cadastrada(s)
        </p>
        <button type="button" className="btn-primary" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          Nova categoria
        </button>
      </div>

      {isLoading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : (data ?? []).length === 0 ? (
        <EmptyState
          icon={Tag}
          title="Nenhuma categoria cadastrada"
          description="Crie categorias para organizar os produtos do seu PDV."
          action={
            <button className="btn-primary" onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Criar primeira categoria
            </button>
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {data!.map((c) => (
            <div key={c.id} className="card flex items-center justify-between gap-3 p-4">
              <div className="flex min-w-0 items-center gap-3">
                <div
                  className="h-10 w-10 shrink-0 rounded-lg"
                  style={{ backgroundColor: c.cor }}
                  aria-hidden
                />
                <div className="min-w-0">
                  <p className="truncate font-medium text-slate-900">{c.nome}</p>
                  <p className="text-xs text-slate-500">
                    Ordem {c.ordem} • {c.ativo ? 'Ativa' : 'Inativa'}
                  </p>
                </div>
              </div>
              <div className="flex shrink-0 gap-1">
                <button
                  type="button"
                  onClick={() => openEdit(c)}
                  className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  aria-label="Editar"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmDelete(c)}
                  className="rounded-lg p-2 text-red-500 hover:bg-red-50"
                  aria-label="Excluir"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <CategoriaFormModal
        open={formOpen}
        categoria={editing}
        onClose={() => setFormOpen(false)}
        loading={createMut.isPending || updateMut.isPending}
        onSubmit={async (input) => {
          if (editing) {
            await updateMut.mutateAsync({ id: editing.id, body: input });
          } else {
            await createMut.mutateAsync(input);
          }
          setFormOpen(false);
        }}
      />

      <ConfirmDialog
        open={!!confirmDelete}
        title="Excluir categoria"
        message={`Deseja realmente excluir "${confirmDelete?.nome}"? Esta acao nao pode ser desfeita.`}
        variant="danger"
        loading={deleteMut.isPending}
        onCancel={() => setConfirmDelete(null)}
        onConfirm={async () => {
          if (confirmDelete) {
            await deleteMut.mutateAsync(confirmDelete.id);
            setConfirmDelete(null);
          }
        }}
      />
    </div>
  );
}
